import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import {
  AD_CARDS_MAX_ACTIVE,
  dateInputToYmd,
  formatYmdDisplay,
  getPublicImageUrl,
  isEffectivelyActive,
  isExpiredByEndDate,
  sortAdCardListItems,
  todayYmdSeoul,
} from './adCardUtils';

const emptySearch = {
  title: '',
  description: '',
  startDate: '',
  endDate: '',
};

const AdCardList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [search, setSearch] = useState(emptySearch);

  const deactivateExpired = useCallback(async () => {
    const today = todayYmdSeoul();
    const { error } = await supabase
      .from('ad_cards')
      .update({ is_active: 'N' })
      .eq('is_active', 'Y')
      .lt('end_ymd', today);
    if (error) throw error;
  }, []);

  const fetchList = useCallback(
    async (filters) => {
      setLoading(true);
      setErrorMessage('');
      try {
        await deactivateExpired();

        let query = supabase
          .from('ad_cards')
          .select(
            'id, title, description, link_url, storage_path, file_name, detail_storage_path, start_ymd, end_ymd, is_active, sort_no, created_at, updated_at'
          );

        if (filters.title.trim()) {
          query = query.ilike('title', `%${filters.title.trim()}%`);
        }
        if (filters.description.trim()) {
          query = query.ilike('description', `%${filters.description.trim()}%`);
        }
        const startYmd = dateInputToYmd(filters.startDate);
        if (startYmd) query = query.eq('start_ymd', startYmd);
        const endYmd = dateInputToYmd(filters.endDate);
        if (endYmd) query = query.eq('end_ymd', endYmd);

        const { data, error } = await query;
        if (error) throw error;
        const today = todayYmdSeoul();
        setItems(sortAdCardListItems(data ?? [], today));
      } catch (error) {
        setErrorMessage(error.message || '목록 조회 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    },
    [deactivateExpired]
  );

  useEffect(() => {
    fetchList(emptySearch);
  }, [fetchList]);

  const handleSearchChange = (event) => {
    const { name, value } = event.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => fetchList(search);

  const handleReset = () => {
    setSearch(emptySearch);
    fetchList(emptySearch);
  };

  const today = todayYmdSeoul();
  const activeCount = items.filter((item) => isEffectivelyActive(item, today)).length;

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
        <div>
          <h6 className="m-0 font-weight-bold text-primary">광고 카드 관리</h6>
          <small className="text-muted">
            today_qt 노출 · 활성 {activeCount}/{AD_CARDS_MAX_ACTIVE} (최대 {AD_CARDS_MAX_ACTIVE}건)
          </small>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => navigate('/ad-cards/new')}
          disabled={loading}
        >
          신규 등록
        </button>
      </div>

      <div className="card-body">
        <div className="form-row align-items-end mb-3">
          <div className="form-group col-md-3 mb-2 mb-md-0">
            <label className="small font-weight-bold mb-1">광고 타이틀</label>
            <input
              type="text"
              className="form-control form-control-sm"
              name="title"
              value={search.title}
              onChange={handleSearchChange}
              placeholder="타이틀 검색"
            />
          </div>
          <div className="form-group col-md-3 mb-2 mb-md-0">
            <label className="small font-weight-bold mb-1">설명</label>
            <input
              type="text"
              className="form-control form-control-sm"
              name="description"
              value={search.description}
              onChange={handleSearchChange}
              placeholder="설명 검색"
            />
          </div>
          <div className="form-group col-md-2 mb-2 mb-md-0">
            <label className="small font-weight-bold mb-1">게시일</label>
            <input
              type="date"
              className="form-control form-control-sm"
              name="startDate"
              value={search.startDate}
              onChange={handleSearchChange}
            />
          </div>
          <div className="form-group col-md-2 mb-2 mb-md-0">
            <label className="small font-weight-bold mb-1">게시 종료일</label>
            <input
              type="date"
              className="form-control form-control-sm"
              name="endDate"
              value={search.endDate}
              onChange={handleSearchChange}
            />
          </div>
          <div className="form-group col-md-2 mb-0 d-flex" style={{ gap: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleSearch} disabled={loading}>
              검색
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset} disabled={loading}>
              초기화
            </button>
          </div>
        </div>

        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-bordered table-hover">
            <thead className="thead-light">
              <tr>
                <th style={{ width: 90 }}>섬네일</th>
                <th>광고 타이틀</th>
                <th>광고 설명</th>
                <th style={{ width: 100 }}>외부링크</th>
                <th style={{ width: 90 }}>상세이미지</th>
                <th style={{ width: 120 }}>게시일</th>
                <th style={{ width: 120 }}>게시 종료일</th>
                <th style={{ width: 110 }}>활성화</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    조회 중...
                  </td>
                </tr>
              ) : items.length ? (
                items.map((item) => {
                  const active = isEffectivelyActive(item, today);
                  const expired = isExpiredByEndDate(item.end_ymd, today);
                  const thumbUrl = item.storage_path
                    ? getPublicImageUrl(supabase, item.storage_path, item.updated_at || item.id)
                    : '';
                  return (
                    <tr
                      key={item.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ad-cards/${item.id}`)}
                    >
                      <td className="text-center align-middle">
                        {thumbUrl ? (
                          <img
                            src={thumbUrl}
                            alt=""
                            style={{ width: 64, height: 40, objectFit: 'cover', borderRadius: 4 }}
                          />
                        ) : (
                          <span className="text-muted small">없음</span>
                        )}
                      </td>
                      <td className="align-middle">{item.title}</td>
                      <td className="align-middle">
                        <span
                          className="d-inline-block text-truncate"
                          style={{ maxWidth: 280 }}
                          title={item.description || ''}
                        >
                          {item.description || '-'}
                        </span>
                      </td>
                      <td className="align-middle text-center">
                        {item.link_url ? (
                          <span className="badge badge-info">있음</span>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td className="align-middle text-center">
                        {item.detail_storage_path ? (
                          <span className="badge badge-secondary">있음</span>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td className="align-middle">{formatYmdDisplay(item.start_ymd)}</td>
                      <td className="align-middle">
                        {formatYmdDisplay(item.end_ymd)}
                        {expired && <div className="small text-danger">종료됨</div>}
                      </td>
                      <td className="align-middle">
                        <span
                          className="badge"
                          style={{
                            backgroundColor: active ? '#28a745' : '#dc3545',
                            color: '#fff',
                            minWidth: 64,
                          }}
                        >
                          {active ? '활성화' : '비활성화'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    조회된 데이터가 없습니다. (0~{AD_CARDS_MAX_ACTIVE}건 노출 가능)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdCardList;
